import { useMemo } from "react";
import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { StatBand, type Stat } from "@components/ui/StatBand";
import { useGetNptRollupQuery, useGetEventActivitiesQuery } from "@api/pApi";
import { useGetResolvedCatalogQuery } from "@api/mApi";
import { fmtDepth, fmtHours, fmtNum, fmtPct } from "@lib/format";
import { intlTag } from "@lib/format";
import type { ActivityOut } from "@api/types";
import type { Locale } from "@i18n/locales";

/** Wall-clock HH:MM for a time-log timestamp, in the tenant's locale. */
function fmtClock(iso: string | null, locale: Locale): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString(intlTag(locale), { hour: "2-digit", minute: "2-digit" });
}

/** NPT?  Prefer the server-derived memo-aware flag (npt_level is unused in this
 * dataset — NPT is tagged in the remark, e.g. "NPT-Formación"); fall back to
 * time_class / npt_level for older payloads. */
function isNpt(a: ActivityOut): boolean {
  if (typeof a.is_npt === "boolean") return a.is_npt;
  if (a.time_class) return a.time_class === "npt";
  const lvl = a.npt_level;
  return lvl != null && Number(lvl) > 0;
}

/**
 * Time log / NPT — the non-productive-time roll-up by cause (server-computed
 * by Oxbo-P via /npt-rollup) plus the activity-code reference from Oxbo-M's
 * resolved catalog. The FE only displays the server's roll-up; it never sums
 * or classifies NPT itself.
 */
export function NptPanel({ eventId, reportId }: { eventId: string; reportId?: string }) {
  const { t, locale } = useLocale();
  const { data, isLoading, isError, refetch } = useGetNptRollupQuery(eventId, {
    skip: !eventId,
  });
  const { data: activities = [] } = useGetEventActivitiesQuery(eventId, { skip: !eventId });
  // On a Daily Report (reportId set) the time log is scoped to THAT day; on the
  // standalone event Time/NPT view it shows every day, grouped by date.
  const visibleActivities = reportId
    ? activities.filter((a) => a.daily_report_id === reportId)
    : activities;
  const groupByDay = !reportId;
  const { data: activityCat } = useGetResolvedCatalogQuery({ categoryKey: "activity_codes" });
  const { data: subcodeCat } = useGetResolvedCatalogQuery({ categoryKey: "activity_subcodes" });

  // entry_key -> display_label lookups from Oxbo-M's resolved catalogs, so the
  // Time Log can show the picklist label (e.g. "«02» Perforación") instead of
  // the bare code. Missing entries fall back to the raw code at render time.
  const codeLabels = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of activityCat?.entries ?? []) m.set(e.entry_key, e.display_label);
    return m;
  }, [activityCat?.entries]);
  const subcodeLabels = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of subcodeCat?.entries ?? []) m.set(e.entry_key, e.display_label);
    return m;
  }, [subcodeCat?.entries]);

  const stats: Stat[] = data
    ? [
        { label: t("npt.kpi.totalHours"), value: fmtHours(data.total_hours, locale) },
        { label: t("npt.kpi.nptHours"), value: fmtHours(data.npt_hours, locale), tone: "warn" },
        {
          label: t("npt.kpi.nptPct"),
          value: fmtPct(data.npt_pct, locale, 1),
          tone: data.npt_pct != null && data.npt_pct > 10 ? "crit" : "warn",
        },
      ]
    : [];

  const rows = data?.by_class ?? [];
  const denom = data?.npt_hours || rows.reduce((s, r) => s + r.total, 0);

  return (
    <QueryState
      isLoading={isLoading}
      isError={isError}
      // Empty only when the event has neither an NPT roll-up nor any time-log
      // rows — an event may carry a time log without any NPT classified yet.
      isEmpty={!!data && rows.length === 0 && activities.length === 0}
      emptyMessage={t("npt.noData")}
      onRetry={refetch}
    >
      <div className="page-head" style={{ marginBottom: 12 }}>
        <p className="subtitle">{t("npt.subtitle")}</p>
      </div>
      {stats.length ? <StatBand stats={stats} /> : null}

      <div style={{ marginTop: 14 }}>
        <div className="card">
          <h3>{t("npt.byCause")}</h3>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t("npt.col.cause")}</th>
                  <th className="num">{t("npt.col.hours")}</th>
                  <th className="num">{t("npt.col.share")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const share = denom ? (r.total / denom) * 100 : 0;
                  return (
                    <tr key={r.key} data-owner="p">
                      <td>{r.key}</td>
                      <td className="num tab-nums">{fmtNum(r.total, locale, { maximumFractionDigits: 1 })}</td>
                      <td className="num">
                        <div className="mini-bar">
                          <span style={{ width: `${Math.min(100, share)}%` }} />
                        </div>
                        <span className="mono" style={{ fontSize: 11 }}>
                          {fmtPct(share, locale)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("npt.timeLog")}</h3>
        {visibleActivities.length === 0 ? (
          <div className="empty-state">{t("npt.log.empty")}</div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th className="num">{t("npt.log.seq")}</th>
                  <th>{t("npt.log.time")}</th>
                  <th className="num">{t("npt.log.duration")}</th>
                  <th>{t("npt.log.phase")}</th>
                  <th>{t("npt.log.code")}</th>
                  <th className="num">{t("npt.log.depth")}</th>
                  <th>{t("npt.log.class")}</th>
                  <th>{t("npt.log.memo")}</th>
                </tr>
              </thead>
              <tbody>
                {visibleActivities.flatMap((a, i) => {
                  const npt = isNpt(a);
                  const prev = visibleActivities[i - 1];
                  const dayHeader =
                    groupByDay && a.report_date && a.report_date !== prev?.report_date ? (
                      <tr key={`day-${a.report_date}-${a.id}`} className="day-group-row">
                        <td colSpan={8} className="day-group">
                          {new Date(`${a.report_date}T00:00:00`).toLocaleDateString(locale, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </td>
                      </tr>
                    ) : null;
                  const row = (
                    <tr key={a.id} data-owner="p">
                      <td className="num tab-nums">{a.sequence_no ?? "—"}</td>
                      <td className="mono" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                        {fmtClock(a.time_from, locale)} – {fmtClock(a.time_to, locale)}
                      </td>
                      <td className="num tab-nums">
                        {fmtNum(a.duration_hours, locale, { maximumFractionDigits: 2 })}
                      </td>
                      <td>{a.phase_entry_key ?? "—"}</td>
                      <td data-owner="m">
                        <span className="mono">
                          {a.activity_code_entry_key
                            ? (codeLabels.get(a.activity_code_entry_key) ??
                              a.activity_code_entry_key)
                            : "—"}
                        </span>
                        {a.activity_subcode_entry_key ? (
                          <span className="mono" style={{ color: "var(--dim)" }}>
                            {" "}
                            /{" "}
                            {subcodeLabels.get(a.activity_subcode_entry_key) ??
                              a.activity_subcode_entry_key}
                          </span>
                        ) : null}
                      </td>
                      <td className="num tab-nums">
                        {a.md_from != null || a.md_to != null
                          ? `${fmtDepth(a.md_from, locale)} – ${fmtDepth(a.md_to, locale)}`
                          : "—"}
                      </td>
                      <td>
                        <span className={`badge ${npt ? "status-completion" : "status-drilling"}`}>
                          {npt
                            ? (a.npt_category ??
                              a.npt_class_entry_key ??
                              a.npt_reason_key ??
                              t("npt.log.npt"))
                            : t("npt.log.productive")}
                        </span>
                      </td>
                      <td style={{ color: "var(--dim)", maxWidth: 280 }}>{a.activity_memo ?? "—"}</td>
                    </tr>
                  );
                  return dayHeader ? [dayHeader, row] : [row];
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </QueryState>
  );
}
